import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Instagram, ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMessage("");

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus("success");
        setEmail("");
      } else {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || "Der opstod en fejl. Prøv venligst igen.");
      }
    } catch (err) {
      setStatus("error");
      setErrorMessage(err.message || "Der opstod en fejl. Prøv venligst igen.");
    }
  };

  return (
    <footer className="bg-white border-t border-slate-100 pt-24 pb-12">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 mb-24">
          {/* Brand */}
          <div className="lg:col-span-4 space-y-8">
            <Link to="/" className="flex items-center gap-4">
              <div className="w-12 h-12 overflow-hidden flex items-center justify-center">
                <img 
                  src="https://assets.auxodev.dk/assets/d91f10e5-8d69-4d90-b5bd-7f70fc130edc/28106c49-a2b8-4b7b-bd26-274e9d86034f.png" 
                  alt="Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <img 
                src="https://assets.auxodev.dk/assets/d91f10e5-8d69-4d90-b5bd-7f70fc130edc/5c2b55a0-9c8e-48b9-a51c-b44d556f6237.png?t=1780554450031" 
                alt="Ta Ja-Hatten På!" 
                className="h-10 w-auto object-contain"
              />
            </Link>
            <p className="text-slate-500 max-w-md text-base leading-relaxed font-light whitespace-pre-line">
              Ta´ JA-Hatten På!
              Vær med til at bryde tavsheden om fordomme og tabuer! Vær nysgerrig, og få en ny bredere viden og forståelse bag om mennesket.
              <span className="text-red-600 font-bold block mt-2">Positive tanker & idéer smitter!</span>
            </p>
            <div className="flex gap-8">
              <a href="https://www.instagram.com/candragaarde/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-red-600 transition-colors flex items-center gap-1 group">
                <span className="text-sm font-bold tracking-widest uppercase">Instagram</span>
                <ArrowUpRight size={14} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <a href="https://www.facebook.com/TaJaHattenPaa/?utm_source=ig&utm_medium=social&utm_content=link_in_bio" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-red-600 transition-colors flex items-center gap-1 group">
                <span className="text-sm font-bold tracking-widest uppercase">Facebook</span>
                <ArrowUpRight size={14} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2 lg:col-start-5">
            <h4 className="font-serif italic text-xl mb-6 text-slate-900">Menu</h4>
            <ul className="space-y-3">
              <li><Link to="/" className="text-slate-500 hover:text-red-600 transition-colors text-sm font-medium tracking-wide">Forside</Link></li>
              <li><Link to="/spoerg-ikke" className="text-slate-500 hover:text-red-600 transition-colors text-sm font-medium tracking-wide">Spørg Ikke</Link></li>
              <li><Link to="/jeg-tilbyder" className="text-slate-500 hover:text-red-600 transition-colors text-sm font-medium tracking-wide">Jeg tilbyder</Link></li>
              <li><Link to="/foredrag" className="text-slate-500 hover:text-red-600 transition-colors text-sm font-medium tracking-wide">Foredrag</Link></li>
              <li><Link to="/om" className="text-slate-500 hover:text-red-600 transition-colors text-sm font-medium tracking-wide">Om Candra</Link></li>
              <li><Link to="/kontakt" className="text-slate-500 hover:text-red-600 transition-colors text-sm font-medium tracking-wide">Kontakt</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-3 lg:col-start-7">
            <h4 className="font-serif italic text-xl mb-6 text-slate-900">Kontakt</h4>
            <ul className="space-y-4">
              <li className="space-y-1">
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">Telefon</p>
                <a href="tel:+4522290935" className="text-slate-900 hover:text-red-600 transition-colors text-base font-light tracking-tight">22 29 09 35</a>
              </li>
              <li className="space-y-1">
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">Email</p>
                <a href="mailto:Kontakt@ja-hatten.dk" className="text-slate-900 hover:text-red-600 transition-colors text-base font-light tracking-tight">Kontakt@ja-hatten.dk</a>
              </li>
              <li className="space-y-1">
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">Lokation</p>
                <p className="text-slate-900 text-base font-light tracking-tight">Danmark</p>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div className="lg:col-span-3 lg:col-start-10 bg-slate-50 p-6 rounded-2xl border border-slate-100/80">
            <h4 className="font-serif italic text-xl mb-3 text-slate-900">Nyhedsbrev</h4>
            <p className="text-xs text-slate-500 leading-relaxed mb-4 font-light">
              Tilmeld dig nyhedsbrevet fra Ta' JA-Hatten På! Selvindsigt med humor
            </p>
            
            {status === "success" ? (
              <div className="bg-emerald-50 border border-emerald-100 text-emerald-800 rounded-xl p-4 flex items-start gap-3">
                <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={16} />
                <div className="text-xs">
                  <p className="font-semibold mb-0.5">Tilmeldt!</p>
                  <p className="text-emerald-700/90">Tak for din tilmelding til vores nyhedsbrev.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                {/* Antispam honeypot */}
                <div className="sr-only absolute overflow-hidden w-0 h-0" aria-hidden="true">
                  <label htmlFor="website_url">Hjemmeside URL</label>
                  <input
                    id="website_url"
                    type="text"
                    name="website_url"
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                {/* Subject field to ensure the email subject is "Nyhedsbrev" */}
                <input type="hidden" name="emne" value="Nyhedsbrev" />
                <input type="hidden" name="subject" value="Nyhedsbrev" />

                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="Din e-mailadresse"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-600/20 focus:border-red-600 transition-all"
                    disabled={status === "loading"}
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full bg-slate-900 hover:bg-red-600 text-white font-medium text-xs uppercase tracking-wider py-2.5 px-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      <span>Tilmelder...</span>
                    </>
                  ) : (
                    <span>Tilmeld</span>
                  )}
                </button>

                {status === "error" && (
                  <p className="text-xs text-red-600 font-medium mt-1">{errorMessage}</p>
                )}
              </form>
            )}
          </div>
        </div>

        <div className="pt-12 border-t border-slate-100 flex flex-col md:flex-row justify-between items-center gap-6 text-[11px] uppercase tracking-[0.2em] font-bold text-slate-400">
          <p>© {currentYear} Ta Ja-Hatten På! — CVR: 46313291</p>
          <div className="flex gap-8">
            <Link to="/privatlivspolitik" className="hover:text-slate-900 transition-colors">Privatlivspolitik</Link>
            <Link to="/handelsbetingelser" className="hover:text-slate-900 transition-colors">Handelsbetingelser</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
